
import { useState } from 'react';

export default function WorkflowSection() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: '01',
      title: 'Data-import en Registratie',
      description: 'Aanmaken en beheren van reizigersprofielen en data-import/validatie.',
      icon: 'ri-database-2-line',
      case: 'Wat als een nieuwe reiziger vervoerd moet worden?',
      details: 'EasyTravel maakt het eenvoudig om nieuwe reizigersprofielen aan te maken en te valideren. Alle relevante data wordt geïmporteerd en gecontroleerd op volledigheid en juistheid.'
    },
    {
      number: '02',
      title: 'Planning',
      description: 'Handmatige planning of via de Planautomaat. Ondersteuning voor routevervoer én vraagafhankelijk vervoer.',
      icon: 'ri-route-line',
      case: 'Flexibele planning voor elke situatie',
      details: 'Of u nu kiest voor handmatige planning of de intelligente Planautomaat, EasyTravel ondersteunt beide. Inclusief feedback op routekwaliteit en het verwerken van grootschalige wijzigingen zoals maximale reistijd en bezetting tijdens epidemieën.'
    },
    {
      number: '03',
      title: 'Monitoring & Events',
      description: 'Realtime volgen van actueel vervoer en het realtime constateren van afwijkingen tussen planning en realisatie.',
      icon: 'ri-eye-line',
      case: 'Directe signalering van afwijkingen',
      details: 'Monitor het vervoer in realtime en ontvang directe meldingen bij afwijkingen. Alle betrokkenen worden automatisch geïnformeerd, zodat snel kan worden bijgestuurd.'
    },
    {
      number: '04',
      title: 'Follow-up & Kwaliteitsborging',
      description: 'Gebruik van de Modules Meldingen & Klachten. Gestructureerde opvolging via "Hoor en wederhoor".',
      icon: 'ri-feedback-line',
      case: 'Reiziger wil zich afmelden',
      details: 'Complete dossiers door gestructureerde opvolging. Directe communicatie met de uitvoerder zorgt voor transparantie en kwaliteitsborging in het hele proces.'
    },
    {
      number: '05',
      title: 'Afrekening',
      description: 'Direct inzicht in de opbrengsten via self-billing overzichten en rapportages.',
      icon: 'ri-file-list-3-line',
      case: 'Transparante financiële controle',
      details: 'Openstaande klachten en meldingen hebben directe consequenties voor de self-billing. Volledige transparantie en controle over uw vervoerskosten.'
    }
  ];

  return (
    <section id="workflow" className="py-12 sm:py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Van Aanvraag tot Afrekening
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            De volledige EasyTravel cyclus in 5 geïntegreerde stappen
          </p>
        </div>

        {/* Timeline Navigation - Mobile Horizontal Scroll */}
        <div className="mb-8 sm:mb-12">
          <div className="flex lg:justify-between overflow-x-auto pb-4 lg:pb-0 relative">
            <div className="hidden lg:block absolute top-8 left-0 right-0 h-1 bg-gray-200"></div>
            <div
              className="hidden lg:block absolute top-8 left-0 h-1 bg-teal-500 transition-all duration-500"
              style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}
            ></div>
            <div className="flex space-x-4 lg:space-x-0 lg:justify-between lg:w-full">
              {steps.map((step, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className="relative z-10 flex flex-col items-center cursor-pointer group flex-shrink-0"
                >
                  <div
                    className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg transition-all ${
                      activeStep === index
                        ? 'bg-teal-500 text-white scale-110'
                        : activeStep > index
                        ? 'bg-teal-500 text-white'
                        : 'bg-white text-gray-400 border-2 border-gray-300'
                    } group-hover:scale-110`}
                  >
                    {step.number}
                  </div>
                  <div className={`mt-2 sm:mt-3 text-xs sm:text-sm font-medium whitespace-nowrap ${
                    activeStep === index ? 'text-teal-600' : 'text-gray-500'
                  }`}>
                    Stap {step.number}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Step Content */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-12 border border-gray-200">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <i className={`${steps[activeStep].icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <div>
                  <div className="text-sm text-teal-600 font-semibold">Stap {steps[activeStep].number}</div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">{steps[activeStep].title}</h3>
                </div>
              </div>
              <p className="text-base sm:text-lg text-gray-700 mb-6 leading-relaxed">
                {steps[activeStep].description}
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg mb-6">
                <div className="text-sm font-semibold text-teal-900 mb-1">Use Case</div>
                <div className="text-teal-800 text-sm sm:text-base">{steps[activeStep].case}</div>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                {steps[activeStep].details}
              </p>
            </div>

            {/* Visual Representation */}
            <div className="relative mt-8 lg:mt-0">
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">Proces Overzicht</h4>
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                </div>
                <div className="space-y-3">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                        index === activeStep
                          ? 'bg-teal-50 border-l-4 border-teal-500'
                          : index < activeStep
                          ? 'bg-green-50 border-l-4 border-green-500'
                          : 'bg-gray-50 border-l-4 border-gray-300'
                      }`}
                    >
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                        index === activeStep
                          ? 'bg-teal-500 text-white'
                          : index < activeStep
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}>
                        {index < activeStep ? (
                          <i className="ri-check-line"></i>
                        ) : (
                          step.number
                        )}
                      </div>
                      <div className="flex-1">
                        <div className={`text-xs sm:text-sm font-medium ${
                          index === activeStep ? 'text-teal-900' : 'text-gray-700'
                        }`}>
                          {step.title}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
